"use client";

/**
 * AnalysePageClient — main page orchestrator for /analyse.
 *
 * - Upload zone with analysis type selection (synced to ?type= URL param)
 * - Partial results display during processing
 * - Job history list (always visible below upload zone)
 */
import { useEffect, useCallback } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useAuth } from "@/platform/auth/useAuth";
import { PageHero } from "@/shared/components/ui/PageHero";
import { UploadZone } from "./UploadZone";
import { JobList } from "./JobList";
import { PartialResults } from "./PartialResults";
import { useAnalyseJobs } from "../hooks/useAnalyseJobs";
import { useUpload } from "../hooks/useUpload";

export function AnalysePageClient() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { user } = useAuth();

  const selectedType = searchParams.get("type") ?? "any";

  const { jobs, isLoading, deleteJob, renameJob } = useAnalyseJobs();
  const { analysisState, handleUpload, resetState } = useUpload();

  // Reset upload state when navigating back to base /analyse
  useEffect(() => {
    if (!searchParams.get("type")) {
      resetState();
    }
  }, [searchParams, resetState]);

  const handleTypeChange = useCallback(
    (type: string) => {
      router.replace(`/analyse?type=${type}`, { scroll: false });
    },
    [router]
  );

  const handleFileSelected = useCallback(
    async (file: File) => {
      if (!user) return; // ProtectedFeature handles the UI gate
      await handleUpload(file, user.uid);
    },
    [user, handleUpload]
  );

  return (
    <>
      <PageHero
        icon={
          <svg
            width="60" height="60" viewBox="0 0 24 24" fill="none"
            stroke="url(#analyse-grad)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="analyse-grad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#5B6CFF" />
                <stop offset="100%" stopColor="#EC4899" />
              </linearGradient>
            </defs>
            <line x1="18" y1="20" x2="18" y2="10" />
            <line x1="12" y1="20" x2="12" y2="4" />
            <line x1="6" y1="20" x2="6" y2="14" />
          </svg>
        }
        title={<><span className="grad-text">sensei.</span> Analyse</>}
        subtitle="Upload any CSV or Excel file for AI-powered analysis, insights, and expert recommendations"
      />

      <div className="page">
        {/* Upload zone */}
        <UploadZone
          analysisState={analysisState}
          selectedType={selectedType}
          onTypeChange={handleTypeChange}
          onFileSelected={handleFileSelected}
          isAuthenticated={!!user}
        />

        {/* Partial results while processing */}
        {(analysisState.partialResults || analysisState.isProcessing) && (
          <div style={{ marginTop: 32 }}>
            <PartialResults analysisState={analysisState} />
          </div>
        )}

        {/* Sample Analysis Reports */}
        <div style={{ marginTop: 48 }}>
          <div className="section-divider">
            <span>Sample Analysis Reports</span>
          </div>
          <div className="sample-grid">
            <div
              className="sample-card s-spend"
              onClick={() => window.open("/analyse/jobs/f8e6b9b0-eb6c-45f3-ab0f-efe303e77074", "_blank")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && window.open("/analyse/jobs/f8e6b9b0-eb6c-45f3-ab0f-efe303e77074", "_blank")}
            >
              <div className="sample-head">
                <span className="sample-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
                  </svg>
                </span>
                <h3>Spend Analysis</h3>
              </div>
              <p className="sample-lead">View sample spend analysis report</p>
              <p className="sample-desc">Explore a comprehensive spend analysis with vendor insights and cost optimization opportunities.</p>
              <span className="sample-cta">
                View report
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </span>
            </div>

            <div
              className="sample-card s-logistics"
              onClick={() => window.open("/analyse/jobs/f6ec74e6-9e5b-4dad-9d90-f1c25a54475e", "_blank")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && window.open("/analyse/jobs/f6ec74e6-9e5b-4dad-9d90-f1c25a54475e", "_blank")}
            >
              <div className="sample-head">
                <span className="sample-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="1" y="3" width="15" height="13" /><polygon points="16 8 20 8 23 11 23 16 16 16 16 8" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />
                  </svg>
                </span>
                <h3>Logistics</h3>
              </div>
              <p className="sample-lead">View sample logistics analysis report</p>
              <p className="sample-desc">Discover logistics performance metrics, delivery analytics, and route optimization insights.</p>
              <span className="sample-cta">
                View report
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </span>
            </div>

            <div
              className="sample-card s-inventory"
              onClick={() => window.open("/analyse/jobs/d704c30a-ed74-43c2-8078-2e3f68e21466", "_blank")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && window.open("/analyse/jobs/d704c30a-ed74-43c2-8078-2e3f68e21466", "_blank")}
            >
              <div className="sample-head">
                <span className="sample-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                  </svg>
                </span>
                <h3>Inventory</h3>
              </div>
              <p className="sample-lead">View sample inventory analysis report</p>
              <p className="sample-desc">Review inventory health metrics, stock levels, and turnover analysis.</p>
              <span className="sample-cta">
                View report
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </span>
            </div>
          </div>
        </div>

        {/* Job history */}
        <div style={{ marginTop: 48 }}>
          <div className="section-divider">
            <span>Your Recent Analyses</span>
          </div>
          <JobList
            jobs={jobs}
            isLoading={isLoading}
            onDelete={deleteJob}
            onRename={renameJob}
          />
        </div>
      </div>
    </>
  );
}
